/**
 * Vendors (VENDOR-01) — read layer over the ESC vendor/PO mirror
 * (mirror_vendor + mirror_vendor_location + mirror_ven_part + mirror_po).
 * Read-only today (ESC is the system of record for vendors until cutover,
 * DEC-22/25); the screen is the foundation for Pegasus-native vendor + PO
 * management. Populated by C:\ESCBACKUP\backfill-vendors-pos.ps1.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/** Editable vendor fields (Pegasus-native overlay over the ESC mirror). */
const OVERRIDE_FIELDS = [
  "name", "first_name", "address1", "address2", "city", "state", "zip",
  "phone", "phone2", "fax", "terms", "account", "discount", "tax_id",
  "email", "web_url", "notes",
] as const;
export type VendorOverride = Partial<Pick<VendorRow, (typeof OVERRIDE_FIELDS)[number]>>;

/** Apply a vendor override on top of the mirror row (edited fields win). */
function applyOverride(base: VendorRow, ov?: Record<string, unknown> | null): VendorRow {
  if (!ov) return base;
  const out = { ...base } as Record<string, unknown>;
  for (const f of OVERRIDE_FIELDS) {
    const v = ov[f];
    if (v != null && v !== "") out[f] = v;
  }
  return out as unknown as VendorRow;
}

export interface VendorRow {
  vendor_code: string;
  name: string | null;
  first_name: string | null;
  address1: string | null; address2: string | null;
  city: string | null; state: string | null; zip: string | null;
  phone: string | null; phone2: string | null; fax: string | null;
  terms: string | null; account: string | null; discount: number | null;
  email: string | null; web_url: string | null; tax_id: string | null;
  notes: string | null; inactive: boolean;
}

export interface VendorContact { name?: string | null; email?: string | null; phone?: string | null; ext?: string | null }
export interface VendorLocation {
  list_id: string; vendor_code: string; loc_name: string | null;
  address1: string | null; address2: string | null; city: string | null; state: string | null; zip: string | null;
  contacts: VendorContact[] | null; inactive: boolean;
}
export interface VenPart {
  vendor_code: string; part: string; ven_part: string | null;
  last_purchase_price: number | null; date_last_purchase: string | null;
}
export interface PoRow {
  po: string; date: string | null; vendor_code: string | null;
  ship_name: string | null; memo: string | null; status: string | null;
  buyer: string | null; terms: string | null; billed: boolean; order_placed: boolean;
}

export function useVendorList(search: string, showInactive: boolean) {
  return useQuery({
    queryKey: ["vendor_list", search.trim().toLowerCase(), showInactive],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<VendorRow[]> => {
      let q = supabase.from("mirror_vendor").select("*");
      if (!showInactive) q = q.eq("inactive", false);
      const s = search.trim();
      if (s) q = q.or(`name.ilike.%${s}%,vendor_code.ilike.%${s}%,city.ilike.%${s}%`);
      const [{ data, error }, { data: ovs }] = await Promise.all([
        q.order("name", { nullsFirst: false }).limit(500),
        supabase.from("pegasus_vendor_override").select("*"),
      ]);
      if (error) throw error;
      const ovMap = new Map<string, Record<string, unknown>>(
        (ovs ?? []).map((o: Record<string, unknown>) => [o.vendor_code as string, o]),
      );
      const mirror = (data ?? []) as VendorRow[];
      const mirrorCodes = new Set(mirror.map((v) => v.vendor_code));
      const merged = mirror.map((v) => applyOverride(v, ovMap.get(v.vendor_code)));
      // Pegasus-native vendors = override rows with NO ESC mirror row (added via "New vendor").
      const sLow = search.trim().toLowerCase();
      const natives: VendorRow[] = [];
      for (const [code, ov] of ovMap) {
        if (mirrorCodes.has(code)) continue;
        const v = applyOverride(blankVendor(code), ov);
        if (!showInactive && v.inactive) continue;
        if (sLow && !`${v.name ?? ""} ${v.vendor_code} ${v.city ?? ""}`.toLowerCase().includes(sLow)) continue;
        natives.push(v);
      }
      return [...natives, ...merged];
    },
  });
}

/** A blank VendorRow (used for Pegasus-native vendors + the create form). */
export function blankVendor(code: string): VendorRow {
  return {
    vendor_code: code, name: null, first_name: null, address1: null, address2: null,
    city: null, state: null, zip: null, phone: null, phone2: null, fax: null,
    terms: null, account: null, discount: null, email: null, web_url: null,
    tax_id: null, notes: null, inactive: false,
  };
}

/** Generate a Pegasus-native vendor code (no ESC code exists for new vendors). */
export const newVendorCode = () => `PV-${Date.now().toString().slice(-7)}`;

export function useVendor(code: string | null) {
  return useQuery({
    queryKey: ["vendor", code],
    enabled: !!code,
    queryFn: async (): Promise<VendorRow | null> => {
      const [{ data }, { data: ov }] = await Promise.all([
        supabase.from("mirror_vendor").select("*").eq("vendor_code", code!).maybeSingle(),
        supabase.from("pegasus_vendor_override").select("*").eq("vendor_code", code!).maybeSingle(),
      ]);
      if (!data) return null;
      return applyOverride(data as VendorRow, ov as Record<string, unknown> | null);
    },
  });
}

/** Save edits to a vendor — Pegasus-native overlay (ESC stays read-only). */
export function useSaveVendorOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { vendor_code: string } & VendorOverride) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_vendor_override").upsert({
        ...v, set_by: u?.user?.email ?? null, set_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor_list"] });
      qc.invalidateQueries({ queryKey: ["vendor"] });
    },
  });
}

export function useVendorLocations(code: string | null) {
  return useQuery({
    queryKey: ["vendor_locations", code],
    enabled: !!code,
    queryFn: async (): Promise<VendorLocation[]> => {
      const { data } = await supabase.from("mirror_vendor_location").select("*").eq("vendor_code", code!);
      return (data ?? []) as VendorLocation[];
    },
  });
}

export function useVendorParts(code: string | null) {
  return useQuery({
    queryKey: ["vendor_parts", code],
    enabled: !!code,
    queryFn: async (): Promise<VenPart[]> => {
      const { data } = await supabase
        .from("mirror_ven_part").select("*").eq("vendor_code", code!)
        .order("date_last_purchase", { ascending: false, nullsFirst: false })
        .limit(500);
      return (data ?? []) as VenPart[];
    },
  });
}

export function useVendorPOs(code: string | null) {
  return useQuery({
    queryKey: ["vendor_pos", code],
    enabled: !!code,
    queryFn: async (): Promise<PoRow[]> => {
      const { data } = await supabase
        .from("mirror_po").select("po,date,vendor_code,ship_name,memo,status,buyer,terms,billed,order_placed")
        .eq("vendor_code", code!).order("date", { ascending: false, nullsFirst: false }).limit(50);
      return (data ?? []) as PoRow[];
    },
  });
}

/** Cost-compare for ONE part across vendors — feeds the item editor. */
export function usePartVendors(part: string | null) {
  return useQuery({
    queryKey: ["part_vendors", part],
    enabled: !!part,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<(VenPart & { vendor_name: string | null })[]> => {
      // ESC per-vendor last price + overlay newer Pegasus-native received purchases
      const [{ data: vp }, { data: peg }] = await Promise.all([
        supabase.from("mirror_ven_part").select("*").eq("part", part!),
        supabase.from("pegasus_purchase").select("vendor_code, unit_cost, received_date").eq("part_number", part!),
      ]);
      const byVendor = new Map<string, VenPart>();
      for (const p of (vp ?? []) as VenPart[]) byVendor.set(p.vendor_code, p);
      for (const r of (peg ?? []) as { vendor_code: string | null; unit_cost: number | null; received_date: string | null }[]) {
        if (!r.vendor_code) continue;
        const ex = byVendor.get(r.vendor_code);
        if (!ex || (r.received_date ?? "") > (ex.date_last_purchase ?? "")) {
          byVendor.set(r.vendor_code, { vendor_code: r.vendor_code, part: part!, ven_part: ex?.ven_part ?? null, last_purchase_price: r.unit_cost, date_last_purchase: r.received_date });
        }
      }
      const parts = [...byVendor.values()];
      if (parts.length === 0) return [];
      const codes = [...new Set(parts.map((p) => p.vendor_code))];
      const { data: vs } = await supabase.from("mirror_vendor").select("vendor_code,name").in("vendor_code", codes);
      const nameByCode = new Map((vs ?? []).map((v: { vendor_code: string; name: string | null }) => [v.vendor_code, v.name]));
      return parts
        .map((p) => ({ ...p, vendor_name: nameByCode.get(p.vendor_code) ?? null }))
        .sort((a, b) => (a.last_purchase_price ?? 1e9) - (b.last_purchase_price ?? 1e9));
    },
  });
}
